import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api.ts";
import { useT } from "../lib/i18n.ts";

/**
 * The needs picker (§4.4): toggle what an item (or place) needs/offers.
 * Built-ins plus the user's own; a small inline "add" keeps custom needs
 * one tap away without a separate management screen.
 */
export function NeedChips(props: {
  selected: string[];
  onChange: (needIds: string[]) => void;
  label: string;
  allowCreate?: boolean;
}) {
  const t = useT();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["needs"], queryFn: api.needs.list });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");

  const createM = useMutation({
    mutationFn: () => api.needs.create(name.trim()),
    onSuccess: async (need) => {
      setName("");
      setAdding(false);
      await qc.invalidateQueries({ queryKey: ["needs"] });
      props.onChange([...props.selected, need.id]);
    },
  });

  const toggle = (id: string) =>
    props.onChange(
      props.selected.includes(id) ? props.selected.filter((x) => x !== id) : [...props.selected, id]
    );

  return (
    <div role="group" aria-label={props.label} style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
      {(data?.needs ?? []).map((n) => (
        <button key={n.id} type="button" className="chip" aria-pressed={props.selected.includes(n.id)} onClick={() => toggle(n.id)}>
          {n.name}
        </button>
      ))}
      {props.allowCreate !== false && !adding && (
        <button type="button" className="chip" onClick={() => setAdding(true)} aria-label={t("needs.add")}>
          + {t("needs.add")}
        </button>
      )}
      {adding && (
        <span style={{ display: "inline-flex", gap: "var(--sp-1)" }}>
          <input
            className="input"
            style={{ width: "10rem" }}
            aria-label={t("needs.add")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (name.trim()) createM.mutate();
              }
            }}
          />
          <button type="button" className="btn" disabled={!name.trim() || createM.isPending} onClick={() => createM.mutate()}>
            {t("add")}
          </button>
        </span>
      )}
    </div>
  );
}
